import { apiClient } from './apiClient';

export interface UploadResponse {
  job_id: string;
  filename: string;
  status: 'pending' | 'uploaded' | 'processing' | 'completed' | 'failed';
  message: string;
  file_size?: number;
  file_type?: string;
}

export interface JobStatus {
  job_id: string;
  filename: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'uploaded';
  progress: number;
  message: string;
  created_at: string;
  updated_at?: string;
  started_at?: string;
  completed_at?: string;
  error?: string | null;
  metadata?: Record<string, any>;
}

export interface JobListResponse {
  jobs: JobStatus[];
  total: number;
  page: number;
  per_page: number;
}

export interface BuildRagResponse {
  status: 'started' | 'already_running';
  message: string;
  documents?: string[];
}

export interface BuildRagStatus {
  running: boolean;
  // 'success' | 'partial: <mensagem>' | 'error: <mensagem>' | null
  last_result: string | null;
  last_run: string | null;
  force_rebuild?: boolean | null;
}

export interface RagStats {
  total_documents: number;
  collections: Array<{
    name: string;
    count: number;
  }>;
  last_updated?: string;
}

class IngestionService {
  private baseUrl = '/ingestion';

  /**
   * Upload document for processing
   */
  async uploadDocument(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`${this.baseUrl}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60s for upload
    });

    return response.data;
  }

  /**
   * Get job status
   */
  async getJobStatus(jobId: string): Promise<JobStatus> {
    const response = await apiClient.get(`${this.baseUrl}/jobs/${jobId}`);
    return response.data;
  }

  /**
   * List all jobs
   */
  async listJobs(page: number = 1, perPage: number = 20): Promise<JobListResponse> {
    const response = await apiClient.get(`${this.baseUrl}/jobs`, {
      params: { page, per_page: perPage },
      timeout: 60000,
    });
    return response.data;
  }

  /**
   * Poll job status until completion
   */
  async pollJobStatus(
    jobId: string,
    onProgress?: (status: JobStatus) => void,
    pollInterval: number = 2000
  ): Promise<JobStatus> {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await this.getJobStatus(jobId);
          
          if (onProgress) {
            onProgress(status);
          }

          if (status.status === 'completed') {
            resolve(status);
          } else if (status.status === 'failed') {
            reject(new Error(status.error || 'Processing failed'));
          } else {
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }

  /**
   * Trigger RAPTOR pipeline to build/update RAG database
   * @param forceRebuild - If true, deletes existing tree and rebuilds from scratch
   */
  async buildRag(forceRebuild: boolean = false): Promise<BuildRagResponse> {
    const response = await apiClient.post(`${this.baseUrl}/build-rag`, null, {
      params: { force_rebuild: forceRebuild },
      timeout: 30000,
    });
    return response.data;
  }

  /**
   * Get RAG build pipeline status
   */
  async getBuildRagStatus(): Promise<BuildRagStatus> {
    const response = await apiClient.get(`${this.baseUrl}/build-rag-status`);
    return response.data;
  }

  /**
   * Get RAG statistics (document count, collections, etc)
   */
  async getRagStats(): Promise<RagStats> {
    const response = await apiClient.get(`${this.baseUrl}/rag-stats`);
    return response.data;
  }

  /**
   * Delete a document from the raw documents directory
   */
  async deleteDocument(filename: string): Promise<{ message: string; filename: string }> {
    const response = await apiClient.delete(`${this.baseUrl}/documents/${encodeURIComponent(filename)}`);
    return response.data;
  }

  async deleteDocuments(filenames: string[]): Promise<{ deleted: string[]; not_found: string[] }> {
    const response = await apiClient.delete(`${this.baseUrl}/documents`, { data: { filenames } });
    return response.data;
  }

  validateFile(file: File): { valid: boolean; error?: string } {
    // Check file size (50MB max)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'File too large. Maximum size is 50MB.',
      };
    }

    // Check file type
    const allowedTypes = [
      'application/pdf', 
      'text/plain',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const allowedExtensions = ['.pdf', '.txt', '.docx'];
    
    const isValidType = allowedTypes.includes(file.type) || 
                      allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isValidType) {
      return {
        valid: false,
        error: 'Invalid file type. Only PDF, TXT and DOCX files are allowed.',
      };
    }

    return { valid: true };
  }
}

export const ingestionService = new IngestionService();
